import * as Sentry from '@sentry/nextjs';
import { type ClassValue, clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import { redirect } from 'next/navigation';
import { twMerge } from 'tailwind-merge';

import type { ApplicationDraft } from '@/lib/schemas/api';
import type { UserData } from '@/lib/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function normalizePhoneNumber(phoneNumber: string) {
  return phoneNumber.trim().replace(/^0/, '+49');
}

function normalizeDateOfBirth(dateOfBirth: string) {
  const parsedDate = parseISO(dateOfBirth);

  if (Number.isNaN(parsedDate.getTime())) {
    return dateOfBirth;
  }
  return format(parsedDate, 'dd.MM.yyyy');
}

const VALID_TENANT_GENDERS = ['male', 'female'] as const;
const VALID_PAYMENT_FREQUENCIES = ['monthly', 'semi_annually', 'quarterly', 'yearly'] as const;
const VALID_APPLICATION_STATES = [
  '',
  'not_completed_not_reservation',
  'reservation_not_accepted',
  'reservation_stopped',
  'reservation_pending',
  'not_completed_reservation',
  'completed_reservation_accepted',
  'completed_not_reservation_accepted',
  'postponed',
  'pending',
  'submitted',
  'submitted_pending',
  'submitted_stopped',
  'cancelled',
  'bounced',
  'accepted',
  'declined',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getStringValue(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function getBooleanValue(value: unknown, fallback = false) {
  return typeof value === 'boolean' ? value : fallback;
}

function getTenantGender(value: unknown): UserData['firstTenant']['gender'] {
  return VALID_TENANT_GENDERS.includes(value as (typeof VALID_TENANT_GENDERS)[number])
    ? (value as UserData['firstTenant']['gender'])
    : '';
}

function getPaymentFrequency(value: unknown): UserData['payment'] {
  return VALID_PAYMENT_FREQUENCIES.includes(value as (typeof VALID_PAYMENT_FREQUENCIES)[number])
    ? (value as UserData['payment'])
    : 'monthly';
}

function getApplicationState(value: unknown): UserData['immutableData']['state'] {
  return VALID_APPLICATION_STATES.includes(value as (typeof VALID_APPLICATION_STATES)[number])
    ? (value as UserData['immutableData']['state'])
    : '';
}

function normalizeTenant(tenant: unknown): UserData['firstTenant'] {
  const tenantRecord = isRecord(tenant) ? tenant : {};
  const addressRecord = isRecord(tenantRecord.address) ? tenantRecord.address : {};

  return {
    gender: getTenantGender(tenantRecord.gender),
    email: getStringValue(tenantRecord.email),
    phoneNumber: normalizePhoneNumber(getStringValue(tenantRecord.phoneNumber)),
    nationality: getStringValue(tenantRecord.nationality),
    dateOfBirth: normalizeDateOfBirth(getStringValue(tenantRecord.dateOfBirth)),
    address: {
      street: getStringValue(addressRecord.street),
      streetNumber: getStringValue(addressRecord.streetNumber),
      city: getStringValue(addressRecord.city),
      zip: getStringValue(addressRecord.zip),
    },
  };
}

function normalizePaymentCondition(condition: unknown) {
  const conditionRecord = isRecord(condition) ? condition : {};

  return {
    rate: getStringValue(conditionRecord.rate),
    monthlyRate: getStringValue(conditionRecord.monthlyRate),
    percentage: getStringValue(conditionRecord.percentage),
  };
}

export function coerceUserData(userData: unknown): UserData | null {
  if (!isRecord(userData)) {
    return null;
  }

  const immutableData = isRecord(userData.immutableData) ? userData.immutableData : null;

  if (!immutableData) return null;
  if (typeof immutableData.hash !== 'string') return null;

  const immutableFirstTenant = isRecord(immutableData.firstTenant) ? immutableData.firstTenant : {};
  const immutableSecondTenant = isRecord(immutableData.secondTenant) ? immutableData.secondTenant : null;
  const apartment = isRecord(immutableData.apartment) ? immutableData.apartment : {};
  const lessor = isRecord(immutableData.lessor) ? immutableData.lessor : {};
  const paymentConditions = isRecord(immutableData.paymentConditions)
    ? immutableData.paymentConditions
    : {};
  const bankAccount = isRecord(userData.bankAccount) ? userData.bankAccount : {};
  const hasSecondTenant = isRecord(userData.secondTenant) || immutableSecondTenant !== null;

  return {
    immutableData: {
      hash: immutableData.hash,
      state: getApplicationState(immutableData.state),
      onFirstDemand: getBooleanValue(immutableData.onFirstDemand),
      firstTenant: {
        name: getStringValue(immutableFirstTenant.name),
      },
      secondTenant: immutableSecondTenant
        ? {
            name: getStringValue(immutableSecondTenant.name),
          }
        : null,
      apartment: {
        address: getStringValue(apartment.address),
        monthly_rent: getStringValue(apartment.monthly_rent),
        deposit_sum: getStringValue(apartment.deposit_sum),
        rental_date: getStringValue(apartment.rental_date),
      },
      lessor: {
        name: getStringValue(lessor.name),
        address: getStringValue(lessor.address),
      },
      paymentConditions: {
        yearly: normalizePaymentCondition(paymentConditions.yearly),
        semi_annually: normalizePaymentCondition(paymentConditions.semi_annually),
        quarterly: normalizePaymentCondition(paymentConditions.quarterly),
        monthly: normalizePaymentCondition(paymentConditions.monthly),
      },
    },
    payment: getPaymentFrequency(userData.payment),
    termsAccepted: getBooleanValue(userData.termsAccepted),
    firstTenant: normalizeTenant(userData.firstTenant),
    bankAccount: {
      holderName: getStringValue(bankAccount.holderName),
      iban: getStringValue(bankAccount.iban),
      bankName: getStringValue(bankAccount.bankName),
    },
    ...(hasSecondTenant
      ? {
          secondTenant: normalizeTenant(userData.secondTenant),
        }
      : {}),
  };
}

export function normalizeUserData(userData: ApplicationDraft): UserData {
  const normalizedData = coerceUserData(userData);

  if (!normalizedData) {
    throw new Error('Invalid userData');
  }

  return normalizedData;
}

export function transformUserData(userData: unknown) {
  if (!isRecord(userData)) {
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      scope.setExtra('userData', userData);
      scope.setTag('location', 'transformUserData');
      Sentry.captureException('Invalid userData');
    });
    redirect('/invalid');
  }

  const firstTenant = isRecord(userData.firstTenant) ? userData.firstTenant : {};
  const secondTenant = isRecord(userData.secondTenant) ? userData.secondTenant : null;

  return {
    ...userData,
    firstTenant: {
      ...firstTenant,
      phoneNumber: normalizePhoneNumber(getStringValue(firstTenant.phoneNumber)),
      dateOfBirth: normalizeDateOfBirth(getStringValue(firstTenant.dateOfBirth)),
    },
    ...(secondTenant
      ? {
          secondTenant: {
            ...secondTenant,
            phoneNumber: normalizePhoneNumber(getStringValue(secondTenant.phoneNumber)),
            dateOfBirth: normalizeDateOfBirth(getStringValue(secondTenant.dateOfBirth)),
          },
        }
      : {}),
  };
}

export function mergeRefs<T>(...refs: React.Ref<T>[]): React.RefCallback<T> {
  return (node: T | null) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T | null>).current = node;
      }
    });
  };
}
